import { addClass, hasClass } from '../util/dom';

export default class BackgroundImageLoader {
    private readonly observer: IntersectionObserver;

    constructor() {
        this.observer = this.initIntersectionObserver();
    }

    public register(items: Element[]): void {
        items.forEach(item => this.observer.observe(item));
    }

    public loadTarget(element: HTMLElement): void {
        if (elementLoaded(element)) {
            return;
        }

        handleLazyBackgroundImage(element);
        handleLazySections(element);
        handleLazySrc(element);
        handleLazySrcSet(element);
        handleLazyPicture(element);

        addClass(element, 'c-lazy-load--loaded');

        this.observer.unobserve(element);
    }

    private initIntersectionObserver(): IntersectionObserver {
        return new IntersectionObserver(entries =>
            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                    this.loadTarget(<HTMLElement>target);
                }
            })
        );
    }
}

function elementLoaded(n: HTMLElement): boolean {
    return hasClass(n, 'c-lazy-load--loaded');
}

function handleLazyBackgroundImage(element: HTMLElement): void {
    const { lazyBackground = null } = element.dataset;
    if (lazyBackground !== null) {
        element.style.backgroundImage = `url('${lazyBackground}')`;
    }
}

function handleLazySections(element: HTMLElement): void {
    const { lazyImageId = null } = element.dataset;
    if (lazyImageId !== null) {
        element.setAttribute('data-image-id', lazyImageId);
        element.removeAttribute('data-lazy-image-id');
    }
}

function handleLazySrc(element: HTMLElement): void {
    const { lazySrc = null } = element.dataset;
    if (lazySrc !== null) {
        element.setAttribute('src', lazySrc);
        element.removeAttribute('data-lazy-src');
    }
}
function handleLazySrcSet(element: HTMLElement): void {
    const { lazySrcset = null } = element.dataset;
    if (lazySrcset !== null) {
        element.setAttribute('srcset', lazySrcset);
        element.removeAttribute('data-lazy-srcset');
    }
}

function handleLazyPicture(element: HTMLElement): void {
    const picture : HTMLPictureElement|null = element.closest('picture');

    if (picture === null) {
        return;
    }

    const sources : HTMLCollectionOf<HTMLSourceElement> = picture.getElementsByTagName('source');
    const img : HTMLCollectionOf<HTMLImageElement> = picture.getElementsByTagName('img');

    Array.from(sources).forEach(element => {
        handleLazySrcSet(element);
    })

    Array.from(img).forEach(element => {
        handleLazySrc(element);
    })
}
